// src/components/TenantManagement.js
//
// SuperAdmin-only screen.
// Creates a Tenant + its first AppAdmin atomically (single batched write).
// Subsequent admins for a tenant are added via UserManagement.
//
// tenantId is auto-generated using the Firestore-issued doc ID.
// SuperAdmin never types or sees an ID at creation time.

import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, Box, TextField, Button, Grid, FormControl,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Fade, Slide, useTheme, alpha, Alert, CircularProgress,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import {
  collection, getDocs, query, where, deleteDoc, doc, updateDoc,
  writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { firestore } from './firebase.config';
import { useAuth } from '../auth/AuthContext';

const blankForm = {
  tenantName: '',
  tenantUrl: '',
  adminName: '',
  adminEmailId: '',
  status: 'active',
};

const TenantManagement = () => {
  const theme = useTheme();
  const { currentUser, isSuperAdmin, loading: authLoading } = useAuth();

  const [formData, setFormData] = useState(blankForm);
  const [tenants, setTenants] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  useEffect(() => {
    if (isSuperAdmin) fetchTenants();
  }, [isSuperAdmin]);

  const fetchTenants = async () => {
    try {
      const snap = await getDocs(collection(firestore, 'Tenants'));
      setTenants(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setErrors({ submission: 'Failed to load tenants.' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const t = {};
    if (!formData.tenantName.trim()) t.tenantName = 'Tenant Name is required';
    if (!formData.tenantUrl.trim()) t.tenantUrl = 'Tenant URL is required';

    if (!selectedTenant) {
      if (!formData.adminName.trim()) t.adminName = 'First Admin Name is required';
      if (!formData.adminEmailId.trim()) {
        t.adminEmailId = 'First Admin Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.adminEmailId)) {
        t.adminEmailId = 'Invalid email format';
      }
    }

    setErrors(t);
    return Object.keys(t).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    const tenantName = formData.tenantName.trim();
    const tenantUrl = formData.tenantUrl.trim();
    const adminEmail = formData.adminEmailId.trim().toLowerCase();
    const adminName = formData.adminName.trim();

    try {
      if (selectedTenant) {
        await updateDoc(doc(firestore, 'Tenants', selectedTenant.id), {
          tenantName,
          tenantUrl,
          status: formData.status,
          updatedBy: currentUser.email,
          updatedAt: serverTimestamp(),
        });
      } else {
        // Admin email must not already exist in UserProfileMaster.
        const dupUser = await getDocs(
          query(
            collection(firestore, 'UserProfileMaster'),
            where('email', '==', adminEmail)
          )
        );
        if (!dupUser.empty) {
          setErrors({
            adminEmailId:
              'A user with this email already exists. Add them via User Management instead.',
          });
          setIsSubmitting(false);
          return;
        }

        // Admin email must NOT be a SuperAdmin (SuperAdmin lives only in .env).
        const superAdminEmails = [
          process.env.REACT_APP_ADMIN_EMAIL1,
          process.env.REACT_APP_ADMIN_EMAIL2,
          process.env.REACT_APP_ADMIN_EMAIL3,
        ]
          .filter(Boolean)
          .map((s) => s.toLowerCase().trim());
        if (superAdminEmails.includes(adminEmail)) {
          setErrors({
            adminEmailId:
              'This email is reserved for a SuperAdmin and cannot be used as a tenant admin.',
          });
          setIsSubmitting(false);
          return;
        }

        // Atomic batched write: tenant + first AppAdmin together.
        // The Firestore-generated doc ID is also stored as `tenantId` in the
        // doc body — that way every other collection (UserProfileMaster,
        // auditReports, assignedChecklists) can reference it the same way.
        const batch = writeBatch(firestore);
        const tenantRef = doc(collection(firestore, 'Tenants'));
        const adminRef = doc(collection(firestore, 'UserProfileMaster'));
        const now = serverTimestamp();

        const tenantId = tenantRef.id;

        batch.set(tenantRef, {
          tenantId,
          tenantName,
          tenantUrl,
          status: formData.status,
          createdBy: currentUser.email,
          createdAt: now,
          updatedBy: currentUser.email,
          updatedAt: now,
        });

        batch.set(adminRef, {
          email: adminEmail,
          displayName: adminName,
          mobileNo: '',
          role: 'AppAdmin',
          tenantId,
          tenantName,
          status: 'active',
          createdBy: currentUser.email,
          createdAt: now,
          updatedBy: currentUser.email,
          updatedAt: now,
        });

        await batch.commit();
      }

      setFormData(blankForm);
      setSelectedTenant(null);
      setSubmitSuccess(true);
      await fetchTenants();
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (err) {
      console.error('Error managing tenant:', err);
      setErrors({ submission: 'Operation failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({
      tenantName: tenant.tenantName || '',
      tenantUrl: tenant.tenantUrl || '',
      adminName: '',
      adminEmailId: '',
      status: tenant.status || 'active',
    });
    setErrors({});
  };

  const handleCancelEdit = () => {
    setSelectedTenant(null);
    setFormData(blankForm);
    setErrors({});
  };

  const handleDelete = async (docId) => {
    if (
      !window.confirm(
        'Delete this tenant? Users belonging to it will become orphaned. This cannot be undone.'
      )
    ) return;
    try {
      await deleteDoc(doc(firestore, 'Tenants', docId));
      fetchTenants();
    } catch (err) {
      console.error('Error deleting tenant:', err);
      setErrors({ submission: 'Failed to delete tenant. Please try again.' });
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">
          You do not have permission to manage tenants. Only SuperAdmin can access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          p: 3,
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
        }}
      >
        <Fade in timeout={800}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 4,
              textAlign: 'center',
            }}
          >
            Tenant Management
          </Typography>
        </Fade>

        {submitSuccess && (
          <Fade in timeout={600}>
            <Alert severity="success" sx={{ mb: 3 }}>
              {selectedTenant ? 'Tenant updated successfully!' : 'Tenant + first admin created!'}
            </Alert>
          </Fade>
        )}

        {errors.submission && (
          <Fade in timeout={600}>
            <Alert severity="error" sx={{ mb: 3 }}>{errors.submission}</Alert>
          </Fade>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Slide direction="up" in timeout={600}>
              <Paper
                sx={{
                  p: 4, mb: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  borderRadius: 3,
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <form onSubmit={handleSubmit}>
                  <Typography variant="h6" gutterBottom>
                    {selectedTenant ? 'Edit Tenant' : 'Create New Tenant'}
                  </Typography>
                  {!selectedTenant && (
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      A unique Tenant ID will be generated automatically.
                      Creating a tenant also creates its first AppAdmin —
                      add more admins later via User Management.
                    </Typography>
                  )}
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl fullWidth error={!!errors.tenantName}>
                        <TextField
                          label="Tenant Name"
                          name="tenantName"
                          value={formData.tenantName}
                          onChange={handleChange}
                          required
                          error={!!errors.tenantName}
                          helperText={errors.tenantName}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth error={!!errors.tenantUrl}>
                        <TextField
                          label="Tenant URL"
                          name="tenantUrl"
                          value={formData.tenantUrl}
                          onChange={handleChange}
                          required
                          error={!!errors.tenantUrl}
                          helperText={errors.tenantUrl || 'e.g. https://acme.example.com'}
                        />
                      </FormControl>
                    </Grid>

                    {!selectedTenant && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mt: 1, color: 'text.secondary' }}>
                            First Admin for this Tenant
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth error={!!errors.adminName}>
                            <TextField
                              label="Admin Name"
                              name="adminName"
                              value={formData.adminName}
                              onChange={handleChange}
                              required
                              error={!!errors.adminName}
                              helperText={errors.adminName}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth error={!!errors.adminEmailId}>
                            <TextField
                              label="Admin Email"
                              name="adminEmailId"
                              type="email"
                              value={formData.adminEmailId}
                              onChange={handleChange}
                              required
                              error={!!errors.adminEmailId}
                              helperText={errors.adminEmailId}
                            />
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        sx={{ mr: 2 }}
                      >
                        {isSubmitting
                          ? 'Saving...'
                          : selectedTenant
                            ? 'Update Tenant'
                            : 'Create Tenant + Admin'}
                      </Button>
                      {selectedTenant && (
                        <Button variant="outlined" onClick={handleCancelEdit}>
                          Cancel Edit
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Slide>
          </Grid>

          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant ID</TableCell>
                    <TableCell>Tenant Name</TableCell>
                    <TableCell>Tenant URL</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <Tooltip title={t.tenantId || ''}>
                          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {t.tenantId ? `${t.tenantId.slice(0, 8)}…` : '—'}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{t.tenantName}</TableCell>
                      <TableCell>{t.tenantUrl || '—'}</TableCell>
                      <TableCell>{t.status}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton color="primary" onClick={() => handleEdit(t)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => handleDelete(t.id)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {tenants.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No tenants yet. Create one above.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default TenantManagement;